import {
	Box,
	ButtonGroup,
	Flex,
	Icon,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Text,
	useDisclosure,
} from '@chakra-ui/react';
import { IconClipboardCheck } from '@tabler/icons';
import { ButtonComponent } from 'components/buttons/ButtonComponent';
import { useChangePageWithConfimation } from 'components/dataTables/agGridTable/PlanningTable/hooks/useChangePageWithConfimation';
import { useExitComponent } from 'components/dataTables/agGridTable/PlanningTable/hooks/useExitComponentWithConfirmation';
import { PlanningTable } from 'components/dataTables/agGridTable/PlanningTable/PlanningTable';
import { IButtonsDefault } from 'components/dataTables/agGridTable/types/agGrid.type';
import { TooltipStandard } from 'components/tooltip/TooltipStandard';
import { useGlobalContext } from 'contexts/GlobalContext';
import { ICycle } from 'models/cycle/cycle.model';
import { useRecalculateRestrictedPlan } from 'pages/admin/capacity/restrictedPlan/hooks/useRecalculateRestrictedPlan';
import { useExistsUnrestrictedPlan } from 'pages/admin/demand/unrestrictedPlan/hooks/useExistsUnrestrictedPlan';
import { useCallback, useEffect, useState } from 'react';

import { useExistsRestrictedPlan } from '../hooks/useExistsRestrictedPlan';
import { useVerifyIsSlow } from '../hooks/useVerifyIsSlow';
import { useVerifyJobIsPending } from '../hooks/useVerifyJobIsPending';
import { ProcessType } from '../types/recalculateRestricted.dto';
import { GeneratePlanConfirmationModal } from './GeneratePlanConfirmationModal';
import { PlanStatusAlert } from './PlanStatusAlert';
import { RecalculateRestrictedPlanModal } from './RecalculateRestrictedPlanModal';
interface IEditRestrictedPlanModalProps {
	isOpen: boolean;
	onClose: () => void;
	cycleId: number | string;
	cycle: ICycle;
}

const EditRestrictedPlanModal = (props: IEditRestrictedPlanModalProps) => {
	const { isOpen, cycleId, cycle, onClose } = props;

	const { data: isSlow } = useVerifyIsSlow({
		cycleId: cycleId as number,
	});
	const {
		isOpen: isOpenRecalculateModal,
		onOpen: onOpenRecalculateModal,
		onClose: onCloseRecalculateModal,
	} = useDisclosure();

	const {
		data: existsRestrictedPlan,
		isLoading: isLoadingStatusExistsRestrictedPlan,
	} = useExistsRestrictedPlan(cycleId);
	const {
		data: existsUnrestrictedPlan,
		isLoading: isLoadingUnrestrictedPlan,
	} = useExistsUnrestrictedPlan(cycleId);

	const [isEnabledVerifyJobIsPending, setIsEnabledVerifyJobIsPending] =
		useState<boolean | undefined>(undefined);

	const onOpenVerifyJobIsPending = useCallback(() => {
		setIsEnabledVerifyJobIsPending(true);
	}, []);

	const { data: isJobPendingData, isFetching: isLoadingIsJobPendingData } =
		useVerifyJobIsPending(
			cycleId as number,
			isEnabledVerifyJobIsPending,
			existsRestrictedPlan
				? ProcessType.RECALCULATE_RESTRICTED_PLAN
				: ProcessType.CALCULATE_RESTRICTED_PLAN
		);

	const isLoadingIsJobPending = isLoadingIsJobPendingData === undefined;

	useEffect(() => {
		if (isJobPendingData?.hasOngoing !== isEnabledVerifyJobIsPending) {
			setIsEnabledVerifyJobIsPending(isJobPendingData?.hasOngoing);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isJobPendingData]);
	const onProcessRunningChange = useCallback(() => {
		onCloseRecalculateModal();
		onOpenVerifyJobIsPending();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	const {
		isOpen: isOpenConfirmationModal,
		onOpen: onOpenConfirmationModal,
		onClose: onCloseConfirmationModal,
	} = useDisclosure();
	const {
		mutate: recalculateRestrictedPlan,
		isPending: isPendingRecalculateRestrictedPlan,
	} = useRecalculateRestrictedPlan(isSlow ?? false, onProcessRunningChange);
	const isJobPending = !!(
		isJobPendingData?.hasOngoing ||
		isPendingRecalculateRestrictedPlan ||
		isLoadingIsJobPending
	);

	const handleOnClose = useCallback(() => {
		onClose();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const renderButtonsTable = ({
		cancelButton,
		updateButton,
	}: IButtonsDefault) => {
		return (
			<>
				{cancelButton}

				<ButtonComponent
					data-test='button-recalcular-editar_plano-modal-dashboard_capacidade'
					type={'secondary'}
					title={'Recalcular'}
					onClick={onOpenRecalculateModal}
					isDisabled={isJobPending || isLoadingIsJobPending}
				/>

				{updateButton}
			</>
		);
	};
	const [isSubmit, setIsSubmit] = useState(false);

	function renderButtons() {
		const tootipeIsPending = (button: React.ReactNode): React.ReactNode => {
			if (isJobPending) {
				return (
					<TooltipStandard
						zIndex={100}
						label='Botão será liberado assim que terminar de processar o plano.'
					>
						{button}
					</TooltipStandard>
				);
			}
			return button;
		};
		const buttonCancel = tootipeIsPending(
			<ButtonComponent
				data-test='button-cancelar-editar_plano-modal-dashboard_capacidade'
				type={'ghost'}
				title={'Cancelar'}
				onClick={handleOnClose}
				isDisabled={isSubmit || isJobPending}
			/>
		);
		const buttonRecalculate = tootipeIsPending(
			<ButtonComponent
				data-test='button-recalcular-editar_plano-modal-dashboard_capacidade'
				type={'secondary'}
				title={'Recalcular'}
				onClick={onOpenRecalculateModal}
				isDisabled={isJobPending || isSubmit}
			/>
		);
		const buttonGenerate = (
			<ButtonComponent
				data-test='button-gerar_plano-editar_plano-modal-dashboard_capacidade'
				type='primary'
				action='submit'
				title='Gerar Plano'
				isDisabled={!existsUnrestrictedPlan || isJobPending}
				onClick={() => {
					if (isSlow) {
						onOpenConfirmationModal();
					} else {
						recalculateRestrictedPlan({
							cycleId: cycleId as any,
							process_type: ProcessType.CALCULATE_RESTRICTED_PLAN,
						});
					}
				}}
			/>
		);
		const buttonUpdate = tootipeIsPending(
			<ButtonComponent
				data-test='button-gerar_plano-editar_plano-modal-dashboard_capacidade'
				type='primary'
				action='submit'
				title='Atualizar Plano'
				onClick={() => setIsSubmit(true)}
				isDisabled={isJobPending}
			/>
		);
		const buttonGroup: {
			condition: boolean | undefined;
			button: React.ReactNode;
		}[] = [
			{ condition: true, button: buttonCancel },
			{
				condition: isJobPending || existsRestrictedPlan,
				button: buttonRecalculate,
			},
			{
				condition: existsRestrictedPlan || isJobPending,
				button: buttonUpdate,
			},
			{
				condition:
					!existsRestrictedPlan &&
					existsUnrestrictedPlan &&
					!isJobPending,
				button: buttonGenerate,
			},
		];
		return (
			<Flex
				justifyContent='end'
				mt='10px'
				mb='20px'
			>
				<ButtonGroup spacing={'10px'}>
					{buttonGroup.map(
						({ condition, button }) => condition && button
					)}
				</ButtonGroup>
			</Flex>
		);
	}
	const { permissionsPage } = useGlobalContext();
	const canEdit =
		!!permissionsPage?.['restrictedPlan']?.editar && !cycle?.is_deprecated;
	const { requestExit } = useExitComponent();
	const { modal: modalExitPage } = useChangePageWithConfimation();
	return (
		<>
			{modalExitPage}
			<Modal
				isOpen={isOpen}
				onClose={() => requestExit(handleOnClose)}
				onEsc={() => requestExit(handleOnClose)}
				isCentered={!existsRestrictedPlan || isJobPending}
				size={existsRestrictedPlan && !isJobPending ? 'full' : '4xl'}
			>
				<ModalOverlay />
				<ModalContent borderRadius={20}>
					<ModalHeader
						display='flex'
						alignItems='end'
						justifyContent='space-between'
						tabIndex={-1}
					>
						<Box>
							<Icon
								as={IconClipboardCheck}
								boxSize={12}
								color={'#25364D'}
							/>
							<Flex
								justifyContent={'space-between'}
								alignItems={'center'}
							>
								<Text
									fontSize='2xl'
									fontWeight={700}
									color={'#25364D'}
								>
									Plano Restrito
								</Text>
							</Flex>
						</Box>
					</ModalHeader>
					<ModalCloseButton data-test='button-fechar-modal' />
					<ModalBody
						display={'flex'}
						flexDirection='column'
						height='100vh'
						gap={'2px'}
						paddingY={0}
					>
						<PlanStatusAlert
							existsRestrictedPlan={existsRestrictedPlan}
							isJobPending={isJobPending}
							existsUnrestrictedPlan={existsUnrestrictedPlan}
							isLoading={
								isLoadingStatusExistsRestrictedPlan ||
								isLoadingUnrestrictedPlan ||
								isEnabledVerifyJobIsPending === undefined
							}
						/>
						{existsRestrictedPlan &&
						!isJobPending &&
						!isLoadingIsJobPending ? (
							<PlanningTable
								nameExport='planoRestrito'
								key={cycle.id}
								cycle={cycle}
								module='restricted-plan'
								canEdit={canEdit}
								renderButtons={renderButtonsTable}
								includeOpportunity={true}
							/>
						) : (
							renderButtons()
						)}
					</ModalBody>
					<RecalculateRestrictedPlanModal
						isOpen={isOpenRecalculateModal}
						onClose={onCloseRecalculateModal}
						cycleId={cycleId as any}
						isSlow={isSlow ?? false}
						onProcessRunningChange={onProcessRunningChange}
					/>
					{!existsRestrictedPlan && existsUnrestrictedPlan && (
						<GeneratePlanConfirmationModal
							isOpen={isOpenConfirmationModal}
							onClose={onCloseConfirmationModal}
							onConfirm={async () => {
								recalculateRestrictedPlan({
									cycleId: cycleId as any,
									process_type:
										ProcessType.CALCULATE_RESTRICTED_PLAN,
								});
								//Pausa de 1 segundo
								await new Promise(resolve =>
									setTimeout(resolve, 500)
								);
								onCloseConfirmationModal();
							}}
						/>
					)}
				</ModalContent>
			</Modal>
		</>
	);
};

export default EditRestrictedPlanModal;
